<template>
  <UButton :aria-label="t('search.button')" class="h-10 gap-3 text-gray-500" color="neutral" icon="i-heroicons-magnifying-glass"
           variant="outline" @click="open = true">
    <span class="hidden md:inline">{{ t("search.button") }}</span>
    <span class="hidden md:flex gap-1">
      <UKbd value="meta" />
      <UKbd>K</UKbd>
    </span>
  </UButton>

  <!-- `title`: without it, the modal's hidden title is not rendered and reka-ui
       reports an accessibility issue. -->
  <UModal v-model:open="open" :ui="{ content: 'sm:max-w-2xl' }"
          :description="t('search.description')" :title="t('search.title')">
    <template #content>
      <UCommandPalette v-model:search-term="term" :groups="groups" :loading="loading" class="h-96" close
                       :placeholder="t('search.placeholder')" @update:open="open = $event">
        <!-- Labels go through slots: the default rendering of Nuxt UI 3.1
             injects `label` as v-html, and a customer name is free input. -->
        <template #order-label="{ item }">
          <span class="inline-flex max-w-full items-baseline gap-2">
            <span class="shrink-0 font-medium">{{ item.order.reference || item.order.id }}</span>
            <span v-if="item.order.isTest" class="shrink-0 text-xs text-gray-400">{{ t("search.test") }}</span>
            <span class="truncate text-xs text-gray-500">{{ subtitle(item.order) }}</span>
          </span>
        </template>
        <template #order-trailing="{ item }">
          <span class="shrink-0 text-xs text-gray-500">{{ t("status.titles." + item.order.status) }}</span>
        </template>
        <template #empty="{ searchTerm }">
          <span v-if="loading">{{ t("search.loading") }}</span>
          <span v-else-if="searchTerm">{{ t("search.none", { term: searchTerm }) }}</span>
          <span v-else>{{ t("search.noOrders") }}</span>
        </template>
      </UCommandPalette>
    </template>
  </UModal>
</template>

<script setup>
import { fetch } from "~/utils/helper";

const props = defineProps({
  navLinks: {
    type: Array,
    default: () => []
  }
});

const { t, locale } = useI18n();
const route = useRoute();

const open = ref(false);
const term = ref("");
const orders = ref([]);
const loading = ref(false);

// Admins search all orders, a customer searches their own.
const isManagement = computed(() => route.path.startsWith("/management"));

// `usingInput`: the shortcut must also work from an input field,
// and close the palette when it is open (its field then has the focus).
defineShortcuts({
  meta_k: {
    usingInput: true,
    handler: () => {
      open.value = !open.value;
    }
  }
});

// A response slower than the next keystroke must not overwrite the list.
let sequence = 0;
async function load() {
  const current = ++sequence;
  loading.value = true;
  try {
    const params = new URLSearchParams({ per_page: "8" });
    if (term.value.trim()) params.set("search", term.value.trim());
    const endpoint = isManagement.value ? "/management/orders" : "/accounts/:accountId/orders";
    const res = await fetch(`${ endpoint }?${ params }`);
    if (current === sequence) orders.value = res?.success ? res.orders : [];
  } catch {
    if (current === sequence) orders.value = [];
  } finally {
    if (current === sequence) loading.value = false;
  }
}

// On open: the latest orders are loaded right away. On typing: wait for
// the end of the input so the API is not queried on every letter.
let timer;
watch([open, term], ([isOpen], [wasOpen]) => {
  clearTimeout(timer);
  if (isOpen) timer = setTimeout(load, wasOpen ? 250 : 0);
});
watch(open, (isOpen) => {
  if (!isOpen) term.value = "";
});

// The orders page opens the detail when it finds `order` in the URL.
function openOrder(order) {
  open.value = false;
  navigateTo({
    path: isManagement.value ? "/management/orders" : "/orders",
    query: { order: order.id, account: order.accountId }
  });
}

function subtitle(order) {
  return [
    order.recipient?.name,
    order.account?.displayname,
    order.createdAt ? new Intl.DateTimeFormat(locale.value, { day: "numeric", month: "short", year: "numeric" }).format(new Date(order.createdAt)) : null
  ].filter(Boolean).join(" · ");
}

const groups = computed(() => [{
  id: "orders",
  label: term.value.trim() ? t("search.orders") : t("search.latest"),
  slot: "order",
  // Sorting is already done by the API: Nuxt UI's local filter would undo it.
  ignoreFilter: true,
  items: orders.value.map((order) => ({
    id: order.id,
    order,
    label: order.reference || order.id,
    icon: "i-heroicons-document-text",
    onSelect: () => openOrder(order)
  }))
}, {
  id: "pages",
  label: t("search.pages"),
  items: props.navLinks.map((link) => ({
    label: link.name,
    icon: "i-heroicons-arrow-right",
    onSelect: () => {
      open.value = false;
      navigateTo(link.url, { external: !!link.external });
    }
  }))
}]);
</script>
